import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { Table, Tag, Tooltip, Skeleton, Checkbox, notification } from "antd"
import { LoadingOutlined, SyncOutlined, ThunderboltOutlined } from "@ant-design/icons"
import moment from "moment"

import {
    fetchEligibleClients,
    selectEligibleClients,
    selectLoading,
    selectSelectedBatchGroupIds,
    selectSelectedClientIds,
    selectSweeping,
    setClientBatchGroups,
    setSelectedClientIds,
    sweepClients,
} from "../clientBatchingSlice"

export default function ClientPickerTable() {
    const dispatch = useDispatch()
    const clients = useSelector(selectEligibleClients)
    const selectedClientIds = useSelector(selectSelectedClientIds)
    const selectedBatchGroupIds = useSelector(selectSelectedBatchGroupIds)
    const loading = useSelector(selectLoading)
    const sweeping = useSelector(selectSweeping)

    const handleSweep = async () => {
        const response = await dispatch(sweepClients(selectedClientIds, selectedBatchGroupIds))

        if (response && response.error) {
            const data = response.error.response && response.error.response.data
            notification.error({
                message: "Sweep not started",
                description: data && data.message ? data.message.join(" ") : "Please try again.",
                duration: 0,
            })
            return
        }

        if (response && response.data && !response.data.client_batch_run) {
            notification.open({
                message: "Nothing to sweep",
                description: response.data.message,
            })
            return
        }

        const totals = response && response.data && response.data.totals
        if (totals) {
            notification.success({
                message: `Swept ${totals.bill_count} bill(s)`,
                description:
                    `${totals.client_count} client(s), ${totals.batch_group_count} batch(es)` +
                    (totals.excepted_bill_count > 0 ? ` · ${totals.excepted_bill_count} withheld` : "") +
                    (totals.flagged_bill_count > 0 ? ` · ${totals.flagged_bill_count} red-flagged (Flagged tab)` : ""),
            })
            dispatch(setSelectedClientIds([]))
            // The swept clients are now held by this run, so the picker's availability is stale
            // until it is re-read.
            dispatch(fetchEligibleClients())
        }
    }

    // A client held by an open run cannot be swept: its bills are already frozen on that run.
    const rowSelection = {
        selectedRowKeys: selectedClientIds,
        onChange: (keys) => dispatch(setSelectedClientIds(keys)),
        getCheckboxProps: (record) => ({
            // A client whose waiting bills are all red-flagged can still be swept: the run holds them
            // on its Flagged tab until they are fixed.
            disabled: !record.available || record.sweepable_bill_count + (record.flagged_bill_count || 0) === 0,
        }),
    }

    // --- batch groups (A/R feedback 5b) -------------------------------------------------------
    // A client absent from selectedBatchGroupIds sweeps all of its groups; an entry means only those.
    const groupsOf = (record) => record.batch_groups || []
    const tickedGroupIds = (record) => {
        if (!selectedClientIds.includes(record.id)) return []
        return selectedBatchGroupIds[record.id] || groupsOf(record).map((group) => group.id)
    }

    const toggleGroup = (record, groupId, checked) => {
        const all = groupsOf(record).map((group) => group.id)
        const current = tickedGroupIds(record)
        const next = checked ? [...new Set([...current, groupId])] : current.filter((id) => id !== groupId)

        if (next.length === 0) {
            dispatch(setSelectedClientIds(selectedClientIds.filter((id) => id !== record.id)))
            return
        }
        if (!selectedClientIds.includes(record.id)) dispatch(setSelectedClientIds([...selectedClientIds, record.id]))
        dispatch(setClientBatchGroups({ clientId: record.id, ids: next.length === all.length ? null : next }))
    }

    // Plain themed markup rather than a nested antd Table: an expanded row inherits antd's light
    // defaults (white background, its own header padding), which the dark theme never restyles.
    const renderGroups = (record) => {
        const ticked = tickedGroupIds(record)
        return (
            <div className="batch-group-picker" role="group" aria-label={`Batch groups for ${record.name}`}>
                <div className="batch-group-picker__row batch-group-picker__row--head">
                    <span />
                    <span>Batch group</span>
                    <span className="batch-group-picker__num">Bills waiting</span>
                    <span className="batch-group-picker__num">Red-flagged</span>
                </div>
                {groupsOf(record).map((group) => {
                    const waiting = group.bill_count - (group.flagged_count || 0)
                    const disabled = !record.available || group.bill_count === 0
                    return (
                        <div
                            key={group.id}
                            className={`batch-group-picker__row${disabled ? " batch-group-picker__row--disabled" : ""}`}
                        >
                            <Checkbox
                                checked={ticked.includes(group.id)}
                                disabled={disabled}
                                onChange={(e) => toggleGroup(record, group.id, e.target.checked)}
                            />
                            <span>{group.name}</span>
                            <span className="batch-group-picker__num">{waiting}</span>
                            <span className="batch-group-picker__num">
                                {group.flagged_count > 0 ? <Tag color="red">{group.flagged_count}</Tag> : "—"}
                            </span>
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderStatus = (record) => {
        if (!record.available) {
            const held = record.blocked_by || {}
            const who = held.held_by_current_user ? "You" : held.user_name || "Another user"
            return (
                <Tooltip title={`Run #${held.client_batch_run_id}`}>
                    <Tag color="orange">
                        {who} · since {held.since ? moment(held.since).format("h:mm A") : "earlier"}
                    </Tag>
                </Tooltip>
            )
        }
        if (record.sweepable_bill_count === 0 && record.flagged_bill_count > 0) {
            return <Tag color="red">Red-flagged only</Tag>
        }
        if (record.sweepable_bill_count === 0) return <Tag>Nothing waiting</Tag>
        return <Tag color="green">Ready</Tag>
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                {/* .card-body is display:flex with `> div { width: 100% }`, so everything inside
                    has to sit in ONE wrapper div or the heading and the table lay out side by
                    side. Same shape as every other table in the app. */}
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Select Clients</h2>
                            <div className="badge">{clients.length}</div>
                        </div>

                        <div className="actions">
                            <div className="table-action-links">
                                <a href="javascript:;" onClick={() => dispatch(fetchEligibleClients())}>
                                    <SyncOutlined spin={loading} />
                                    Refresh
                                </a>

                                {selectedClientIds.length > 0 && !sweeping && (
                                    <a href="javascript:;" onClick={handleSweep}>
                                        <ThunderboltOutlined />
                                        Sweep {selectedClientIds.length} client
                                        {selectedClientIds.length === 1 ? "" : "s"}
                                    </a>
                                )}

                                {sweeping && (
                                    <span>
                                        <LoadingOutlined /> Sweeping…
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <Skeleton active loading={loading && clients.length === 0}>
                        <Table
                            className="client-picker-table"
                            dataSource={clients}
                            rowKey={(r) => r.id}
                            rowSelection={rowSelection}
                            pagination={false}
                            // A nested table per client, NOT tree `children`: tree data would fold
                            // the group rows into this table's rowSelection and collide on rowKey.
                            expandable={{
                                expandedRowRender: renderGroups,
                                rowExpandable: (record) => record.uses_batch_groups && groupsOf(record).length > 0,
                            }}
                        >
                            <Table.Column title="Client" key="name" dataIndex="name" />
                            <Table.Column title="Code" key="short_code" dataIndex="short_code" />
                            <Table.Column
                                title="Bills waiting"
                                key="sweepable_bill_count"
                                dataIndex="sweepable_bill_count"
                            />
                            <Table.Column
                                title="Batch groups"
                                key="batch_group_count"
                                render={(text, record) => {
                                    if (!record.uses_batch_groups) return "—"
                                    const partial = selectedBatchGroupIds[record.id]
                                    return partial ? (
                                        <Tooltip title="Only the ticked batch groups will be swept. Expand the row to change.">
                                            <Tag color="blue">
                                                {partial.length} of {groupsOf(record).length}
                                            </Tag>
                                        </Tooltip>
                                    ) : (
                                        record.batch_group_count
                                    )
                                }}
                            />
                            <Table.Column
                                title="Withheld"
                                key="ungrouped_division_bill_count"
                                render={(text, record) =>
                                    record.ungrouped_division_bill_count > 0 ? (
                                        <Tooltip title="Bills in divisions with no batch group. They are left unswept until a group is assigned.">
                                            <Tag color="red">{record.ungrouped_division_bill_count}</Tag>
                                        </Tooltip>
                                    ) : (
                                        "—"
                                    )
                                }
                            />
                            <Table.Column
                                title="Red-flagged"
                                key="flagged_bill_count"
                                render={(text, record) =>
                                    record.flagged_bill_count > 0 ? (
                                        <Tooltip title="Current charges do not add up. These are held out of the run, on its Flagged tab, until fixed.">
                                            <Tag color="red">{record.flagged_bill_count}</Tag>
                                        </Tooltip>
                                    ) : (
                                        "—"
                                    )
                                }
                            />
                            <Table.Column title="Status" key="status" render={(text, record) => renderStatus(record)} />
                        </Table>
                    </Skeleton>
                </div>
            </div>
        </div>
    )
}
